import { useEffect, useState } from "react";
import { Stack, Tooltip, Typography } from "@mui/material";
import { getVersions, VersionResponse } from "../../api/version";

interface Props {
  collapsed: boolean;
}

// Small footer in the desktop drawer (and a compact form in the mobile
// header) showing the operator + portal pod versions. Two values
// because the Deployments roll independently — pinning them both to
// the same tag is the operator's intent but mid-rollout they differ,
// and an unhealthy state (operator pod down) surfaces here as
// "operator unreachable".

export function VersionBadge({ collapsed }: Props) {
  const [v, setV] = useState<VersionResponse | null>(null);

  useEffect(() => {
    let alive = true;
    getVersions()
      .then((r) => {
        if (alive) setV(r);
      })
      .catch(() => {
        if (alive) setV({ portal_version: "unknown", operator_version: "unreachable" });
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!v) {
    return collapsed ? null : (
      <Typography variant="caption" sx={{ opacity: 0.4, fontSize: "0.6rem" }}>
        loading...
      </Typography>
    );
  }

  // Collapsed mode (icon-only drawer): show a short matched-versions
  // badge if portal === operator, else a divergence indicator. Full
  // tooltip on hover.
  if (collapsed) {
    const matched = v.portal_version === v.operator_version;
    const label = matched ? v.portal_version : "⚠";
    return (
      <Tooltip
        title={`operator ${v.operator_version} | portal ${v.portal_version}`}
        placement="right"
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.55rem",
            opacity: 0.5,
            textAlign: "center",
            display: "block",
            fontFamily: "monospace",
          }}
        >
          {label}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Stack spacing={0.25} sx={{ opacity: 0.5, fontFamily: "monospace" }}>
      <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
        operator {v.operator_version}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
        portal&nbsp;&nbsp; {v.portal_version}
      </Typography>
    </Stack>
  );
}
